/**
 * #427 W3 — pay-rate history + set-a-rate (spec §5 W3, D8/G4).
 *
 * Rates power the missed-rest $ math and the export gross. History is
 * append-only and owner-gated: only a holder of `payroll.rates:write:all`
 * (owner) sees the form; everyone with `payroll:read:all` can read the
 * history. A positive-dollars entry → integer cents (the server rejects a
 * $0/negative rate; we mirror that in the form so it never submits one).
 *
 * Five states: loading skeleton · error (retry) · empty (teaching copy +
 * the set-a-rate action) · default (history) · pending (saving). Stale N/A —
 * the modal refetches on open and after every save.
 */
import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@ferro/ui';
import { adminPayrollApi, type PayRateView } from '../../lib/api.js';
import { todayIsoDate } from '../../lib/api.js';
import {
  centsToDollarsInput,
  dollarsInputToCents,
  formatUsdCents,
} from '../../lib/timesheet-format.js';

export interface PayRatesModalProps {
  apiUrl: string;
  staffUserId: string;
  staffName: string;
  /** Owner only (payroll.rates:write:all). Others see read-only history. */
  canEdit: boolean;
  onClose: () => void;
  /** Fired after a successful save so the page can recompute the row. */
  onSaved: () => void;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; rates: PayRateView[] };

function fmtEffective(dateKey: string): string {
  const d = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateKey;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function PayRatesModal({
  apiUrl,
  staffUserId,
  staffName,
  canEdit,
  onClose,
  onSaved,
}: PayRatesModalProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [rateInput, setRateInput] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(() => todayIsoDate());
  const [pending, setPending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setState({ kind: 'loading' });
    const result = await adminPayrollApi.getRates(apiUrl, staffUserId);
    if (result.error !== undefined || !result.data) {
      setState({
        kind: 'error',
        message: result.error ?? 'We couldn’t load the rate history.',
      });
      return;
    }
    // Newest effective date first, so "Current" reads at the top.
    const rates = [...result.data.rates].sort((a, b) =>
      b.effective_from.localeCompare(a.effective_from),
    );
    setState({ kind: 'ready', rates });
  }, [apiUrl, staffUserId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (): Promise<void> => {
    const cents = dollarsInputToCents(rateInput);
    if (cents === null) {
      setFormError('Enter an hourly rate greater than $0.');
      return;
    }
    setPending(true);
    setFormError(null);
    const result = await adminPayrollApi.setRate(apiUrl, staffUserId, {
      hourlyRateCents: cents,
      effectiveFrom,
    });
    setPending(false);
    if (result.error !== undefined || !result.data) {
      setFormError(result.error ?? 'We couldn’t save that rate. Try again.');
      return;
    }
    setRateInput('');
    await load();
    onSaved();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Pay rates — ${staffName}`}
      size="md"
    >
      {state.kind === 'loading' ? (
        <div className="ferro-skeleton-stack" data-testid="rates-skeleton">
          <div className="ferro-skeleton ferro-skeleton--text" />
          <div className="ferro-skeleton ferro-skeleton--text" />
        </div>
      ) : state.kind === 'error' ? (
        <div className="ferro-alert ferro-alert--error" role="alert">
          <span>{state.message}</span>
          <button
            type="button"
            className="ferro-btn ferro-btn--sm ferro-btn--outline"
            onClick={() => void load()}
          >
            Try again
          </button>
        </div>
      ) : (
        <>
          {state.rates.length === 0 ? (
            <p className="ferro-ts-modal__lead" data-testid="rates-empty">
              No pay rate set yet.{' '}
              {canEdit
                ? 'Set one below to turn on payroll math for this person.'
                : 'An owner needs to set one before payroll can be calculated.'}
            </p>
          ) : (
            <ul className="ferro-ts-rates" data-testid="rates-history">
              {state.rates.map((r, idx) => (
                <li key={`${r.effective_from}-${idx}`} className="ferro-ts-rates__item">
                  <span className="ferro-ts-rates__amount ferro-ts-tnum">
                    {formatUsdCents(r.hourly_rate_cents)}/hr
                  </span>
                  <span className="ferro-ts-rates__from">
                    from {fmtEffective(r.effective_from)}
                    {idx === 0 ? (
                      <span className="ferro-ts-chip ferro-ts-chip--current">
                        Current
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {canEdit ? (
            <form
              className="ferro-form ferro-ts-rate-form"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <div className="ferro-ts-rate-form__grid">
                <label className="ferro-field">
                  <span className="ferro-field__label">Hourly rate (USD)</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0.01"
                    step="0.01"
                    className="ferro-field__input"
                    value={rateInput}
                    onChange={(e) => setRateInput(e.target.value)}
                    placeholder={
                      state.rates[0]
                        ? centsToDollarsInput(state.rates[0].hourly_rate_cents)
                        : '15.50'
                    }
                    data-testid="rate-amount-input"
                    required
                  />
                </label>
                <label className="ferro-field">
                  <span className="ferro-field__label">Effective from</span>
                  <input
                    type="date"
                    className="ferro-field__input"
                    value={effectiveFrom}
                    onChange={(e) =>
                      setEffectiveFrom(e.target.value || todayIsoDate())
                    }
                    data-testid="rate-effective-input"
                    required
                  />
                </label>
              </div>
              {formError ? (
                <p className="ferro-form__error" role="alert" data-testid="rate-error">
                  {formError}
                </p>
              ) : null}
              <div className="ferro-ts-modal__actions">
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={onClose}
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="ferro-btn ferro-btn--primary"
                  disabled={pending}
                  data-testid="rate-save"
                >
                  {pending ? 'Saving…' : 'Save rate'}
                </button>
              </div>
            </form>
          ) : (
            <div className="ferro-ts-modal__actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
